import { memo } from 'react';

import { type AssetCustomFieldValue, type BoardCustomFieldValue } from '@air/api/types';

import Linkify from '~/components/Linkify';
import { ResizableEditableTextInput } from '~/components/ResizableEditableTextInput';
import { CUSTOM_FIELD_PLAIN_TEXT } from '~/constants/testIDs';
import { useExternalLinksClickableContext } from '~/providers/ExternalLinksClickableProvider';

export type CustomFieldPlainTextProps = {
  field: BoardCustomFieldValue | AssetCustomFieldValue;
  onSubmit: ((value: string) => void) | undefined;
};

export const CustomFieldPlainText = memo(({ field, onSubmit }: CustomFieldPlainTextProps) => {
  const { areExternalLinksClickable } = useExternalLinksClickableContext();

  if (onSubmit) {
    return (
      <ResizableEditableTextInput
        label="Field value"
        placeholder="None"
        // biome-ignore lint/plugin: Cypress selects this plain-text field by the field name baked into the test id
        data-testid={`${CUSTOM_FIELD_PLAIN_TEXT}-${field.name}`}
        defaultValue={field.plainTextValue}
        onSubmit={onSubmit}
        appearance="outline"
      />
    );
  }

  return (
    <Linkify disable={!areExternalLinksClickable}>
      <div
        data-testid="READONLY_CUSTOM_FIELD_PLAIN_TEXT"
        data-fieldname={field.name}
        className="whitespace-pre-wrap break-normal px-2 py-1 ads:text-secondary text-14 text-gray-11 leading-normal"
      >
        {field.plainTextValue || 'None'}
      </div>
    </Linkify>
  );
});

CustomFieldPlainText.displayName = 'CustomFieldPlainText';
